import {AjaxStatusCore, WebserviceCore} from '@utahdts/dts-react-common';
import {buildDropDownOptions, dispatchUpdateData} from '../misc/Shared';
import reduxStore from '../app/ReduxStore';
import {iconWarning} from "../common/Icon";

export const ajaxStatus = new AjaxStatusCore();

const CONTEXT_URL = {
	// LOCAL: 'http://localhost:8081/cobiWS/api/',
	LOCAL: 'https://cobi-ws.at.utah.gov/api/',
	DEV: 'https://cobi-ws.dev.utah.gov/api/',
	AT: 'https://cobi-ws.at.utah.gov/api/',
	PROD: 'https://cobi-ws.utah.gov/api/'
};

export const currentContext = (document.location.hostname.includes('localhost')) ? CONTEXT_URL.LOCAL
	: (document.location.hostname.includes('.dev.')) ? CONTEXT_URL.DEV
		: (document.location.hostname.includes('.at.')) ? CONTEXT_URL.AT
			: CONTEXT_URL.PROD;

/**
 * An axios catch() error handler
 * Note: Will also need to import showMessage() & app store when used.
 * @param error - Error object from axios.
 * @param displayError {boolean} - show the error to the user?
 */
export const axiosErrorHandler = (error, displayError) => {
	let errorMessage = {
		type: 'Error',
		title: 'An Error Has Occurred',
		timestamp: new Date().toLocaleString('en-US'),
	};

	if (!error.request && !error.response) {
		errorMessage.shortDescription = error.message;
		errorMessage.longDescription = error.stack;
		errorMessage.context = 'General javascript error';
	} else if (!error.response) {
		// console.log("axiosRequest networkError", error);
		errorMessage.shortDescription = "axiosRequest cannot connect to server.";
		errorMessage.context = error.request !== undefined ? error.request.responseURL : '';
	} else {
		// console.log("axiosRequest error", error);
		// console.log("axiosRequest status", error.response.status);
		// console.log('axiosRequest failed', error.response.data);

		if (typeof error.response.data === 'object') {
			const objError = error.response.data;
			errorMessage.code = objError.status;
			errorMessage.shortDescription = objError.error;
			errorMessage.longDescription = objError.message;
			if (objError.errors) {
				errorMessage.additionalDetail = objError.errors[0].defaultMessage;
			}
			errorMessage.context = objError.path;
			errorMessage.timestamp = objError.timestamp;
		} else {
			// generic server error
			errorMessage.code = error.response.status;
			errorMessage.shortDescription = error.message;
			errorMessage.context = error.request.responseURL;
			errorMessage.timestamp = error.response.headers.date;
		}
	}

	dispatchUpdateData({field: 'messages', value: reduxStore.getState().messages.concat(errorMessage)});
	if (displayError) {

		// showMessage(errorMessage);
		// // log to the js logger
		// const logger = jsLogging({url: 'public-api/log/error',});
		//logger.log(errorMessage);
	}
	//pass error down the line?
	//throw error;
};

const CobiClientWs = new WebserviceCore({
	baseUrl: '',
	ajaxStatusCore: ajaxStatus,
	jsLogging: undefined,
});

const CobiMainWS = new WebserviceCore({
	baseUrl: currentContext,
	ajaxStatusCore: ajaxStatus,
	jsLogging: undefined,
});

const CobiSecureWS = new WebserviceCore({
	baseUrl: currentContext,
	ajaxStatusCore: ajaxStatus,
	jsLogging: undefined,
	allResultsCallback: data => {
		// pull out roles and check error message
		if (data.errors && data.errors.length) {
			data.errors.forEach((value) => {
				const message = {
					title: 'Webservice Error',
					message: value,
					icon: iconWarning
				};
				dispatchUpdateData({field: 'messages', value: reduxStore.getState().messages.concat(message)});
			});
		} else if (!data) {
			const message = {
				title: 'Authentication Error',
				message: 'Call did not return updated security',
				icon: iconWarning
			};
			dispatchUpdateData({field: 'messages', value: reduxStore.getState().messages.concat(message)});
		} else {
			dispatchUpdateData({field: 'userAccount', value: data});
		}
		return data && data.data;
	},
	loadDefaultsCallback: defaults => {
		defaults.headers.common['Authorization'] = reduxStore.getState().user ? `Bearer ${reduxStore.getState().user.access_token}`: false;
	},

});

export const WEBSERVICE_AJAX_ID = {
	MENU_BY_ID: 'menuById',
};

const webservice = {
	handleError: {
		sendError: error => CobiMainWS.post('error.json', error),
	},
	public: {
		navigation: {
			menuById: (cobiId, fiscalYear) => CobiMainWS.get(`navigation/menu/${fiscalYear}/${cobiId}.json`,undefined,WEBSERVICE_AJAX_ID.MENU_BY_ID),
            fiscalYears: () => CobiMainWS.get(`navigation/fiscalYears.json`).then(years => {dispatchUpdateData([
            	{path: 'app', field: 'fiscalYears', value: years.fiscalYears},
            	{path: 'app', field: 'currentFiscalYear', value: years.currentFiscalYear},
            	{path: 'app', field: 'currentEditorFiscalYear', value: years.currentEditorFiscalYear},
            	]); return years;}),
		},
		tab: {
			tabsById: (cobiId, fiscalYear) => CobiMainWS.get(`tab/${fiscalYear}/${cobiId}.json`),
			content: (cobiId, fiscalYear, tab) => CobiMainWS.get(`tab/${tab}/${fiscalYear}/${cobiId}.json`),
			chart: (cobiId, fiscalYear, category, appType, chartType) => CobiMainWS.get(`tab/financials/${chartType}/${fiscalYear}/${category}/${appType}/${cobiId}.json`),
		},
		fund: {
			get: (fundId) => CobiMainWS.get(`fund/${fundId}.json`),
			fundsList: () => CobiMainWS.get(`lookup/funds.json`),
		},
		issue: {
			get: (fiscalYear, cobiId, itemKey) => CobiMainWS.get(`tab/issues/detail/${fiscalYear}/${cobiId}/${itemKey}.json`),
			related: (fiscalYear, cobiId, itemKey) => CobiMainWS.get(`tab/issues/detail/related/${fiscalYear}/${cobiId}/${itemKey}.json`),
		},
		otherAction: {
			get: (fiscalYear, cobiId, issueId) => CobiMainWS.get(`tab/issues/${fiscalYear}/${cobiId}/issueText/${issueId}.json`),
			getDraft: (fiscalYear, cobiId, issueId) => CobiMainWS.get(`tab/issues/draft/${fiscalYear}/${cobiId}/issueText/${issueId}.json`),
			otherActionTypes: (fiscalYear) => CobiMainWS.get(`lookup/${fiscalYear}/otherActionTypes.json`)
		},
		list: {
            get: (fiscalYear) => CobiMainWS.get(`dashboard/${fiscalYear}/list.json`),
			filter: (fiscalYear, cobiId) => CobiMainWS.get(`dashboard/${fiscalYear}/${cobiId}/filter.json`),
            fiscalYears: () => CobiMainWS.get(`navigation/fiscalYears.json`).then((years) => {return buildDropDownOptions(years);}),
            menuById: (cobiId, fiscalYear) => CobiMainWS.get(`navigation/menu/${fiscalYear}/${cobiId}.json`),
		},
		version: {
			getClient:() => CobiClientWs.get(`version.json`),
			getWs:() => CobiMainWS.get(`version.txt`),
		}
	},
	admin: {
		navigation: {
			menuById: (cobiId, fiscalYear) => CobiMainWS.get(`navigation/menu/draft/${fiscalYear}/${cobiId}.json`,undefined,WEBSERVICE_AJAX_ID.MENU_BY_ID),
		},
		tab: {
			draft: (cobiId, fiscalYear, tab) => CobiMainWS.get(`tab/${tab}/draft/${fiscalYear}/${cobiId}.json`),
			update: data => CobiMainWS.put('secure/editor/tab/draft/update', data),
			submit: (tab, fiscalYear, cobiId) => CobiMainWS.put(`secure/editor/submit/${tab}/${fiscalYear}/${cobiId}`),
			approve: (tab, fiscalYear, cobiId) => CobiMainWS.put(`secure/editor/approve/${tab}/${fiscalYear}/${cobiId}`),
			revise: (tab, fiscalYear, cobiId) => CobiMainWS.put(`secure/editor/revise/${tab}/${fiscalYear}/${cobiId}`),
			active: (tab, fiscalYear, cobiId) => CobiMainWS.put(`secure/editor/update/active/${tab}/${fiscalYear}/${cobiId}`),
		},
		cobiId: {
			active: (fiscalYear, cobiId) => CobiMainWS.put(`secure/editor/update/active/${fiscalYear}/${cobiId}`),
		},
		s3: {
			get: (dateStamp, regionName)=> CobiMainWS.get(`secure/editor/${dateStamp}/${regionName}/s3.json`),
		},
		user: {
			get: () => CobiSecureWS.get('secure/user'),
		},
		statutes: {
			majorTypes: () => CobiMainWS.get('secure/editor/statute/majorTypes.json'),
			minorTypes: uidValue => CobiMainWS.get(`secure/editor/statute/${uidValue}/minorTypes.json`),
			get: statuteId => CobiMainWS.get(`secure/editor/statute/${statuteId}.json`),
			add: data => CobiMainWS.post(`secure/editor/statute/add`, data),
			update: (statureId, data) => CobiMainWS.post(`secure/editor/statute/update/${statureId}`, data)
		},
		datalist: {
			statuses: () => CobiMainWS.get(`lookup/statuses.json`),
			analysts: () => CobiMainWS.get(`lookup/analysts.json`),
			supervisors: () => CobiMainWS.get(`lookup/supervisors.json`),
		},
		comment: {
			get: (tab, fiscalYear, cobiId) => CobiMainWS.get(`secure/editor/comments/${tab}/${fiscalYear}/${cobiId}.json`),
			post: data => CobiMainWS.post(`secure/editor/comment/add`, data),
		},
		otherAction: {
			create: item => CobiMainWS.post(`secure/editor/action/item/add`, item),
			update: item => CobiMainWS.post(`secure/editor/action/item/update`, item)
		},
		performance: {
			get: (fiscalYear, cobiId) => CobiMainWS.get(`secure/editor/graph/${fiscalYear}/${cobiId}.json`),
			create: item => CobiMainWS.post(`secure/editor/graph/add`, item),
			update: item => CobiMainWS.post(`secure/editor/graph/update`, item)
		}
	}
};

export default webservice;
